/**
 * Shared i18n helpers for service ticket enums.
 */
import { formatApiDate } from '../utils/formatApiDate';

export const SERVICE_STAGES = ['CREATED', 'ASSIGNED', 'IN_PROGRESS', 'DONE', 'REVIEW_FAILED', 'CLOSED'];
export const SERVICE_TYPES = ['OFFLINE', 'EMPTY', 'FULL', 'OTHER'];
export const SERVICE_PAYMENTS = ['NOT_APPLICABLE', 'PENDING', 'APPROVED_FOR_PAY', 'PAID'];

/** Queue chips on /service/tickets. Default = review (DONE). */
export const QUEUE_CHIPS = [
  { id: 'review', stage: 'DONE', labelKey: 'service.queueReview' },
  { id: 'progress', stage: 'IN_PROGRESS', labelKey: 'service.queueProgress' },
  { id: 'rework', stage: 'REVIEW_FAILED', labelKey: 'service.queueRework' },
  { id: 'closed', stage: 'CLOSED', labelKey: 'service.queueClosed' },
  { id: 'all', stage: '', labelKey: 'service.queueAll' },
];

export function shortTicketId(id) {
  if (id == null || id === '') return '—';
  return String(id).slice(-6).toUpperCase();
}

export function typeLabel(t, type) {
  const map = {
    OFFLINE: t('service.typeOffline'),
    EMPTY: t('service.typeEmpty'),
    FULL: t('service.typeFull'),
    OTHER: t('service.typeOther'),
  };
  return map[type] || type || '—';
}

export function stageLabel(t, stage) {
  const map = {
    CREATED: t('service.stageCreated'),
    ASSIGNED: t('service.stageAssigned'),
    IN_PROGRESS: t('service.stageInProgress'),
    DONE: t('service.stageDone'),
    ASSESSOR_REVIEW: t('service.stageDone'),
    REVIEW_FAILED: t('service.stageReviewFailed'),
    CLOSED: t('service.stageClosed'),
  };
  return map[stage] || stage || '—';
}

export function technicianOutcome(comments = []) {
  for (let i = comments.length - 1; i >= 0; i -= 1) {
    const text = String(comments[i]?.text || '');
    const match = text.match(/Исход:\s*(.+)/i) || text.match(/Outcome:\s*(.+)/i);
    if (match) return match[1].split('\n')[0].trim();
  }
  return null;
}

export function parseCheckedFromComments(comments = []) {
  for (let i = comments.length - 1; i >= 0; i -= 1) {
    const text = String(comments[i]?.text || '');
    const match = text.match(/Проверено:\s*(.+)/i) || text.match(/Checked:\s*(.+)/i);
    if (!match) continue;
    const rest = match[1].split('\n')[0].trim();
    if (!rest || /^\d+\s*\/\s*\d+$/.test(rest)) continue;
    return rest.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

/** BOOL diagnostics for the report card. Hides the work_done gate; falls back to the complete comment. */
export function diagnosisItems(ticket) {
  const checklist = ticket?.checklist || [];
  const diagnostics = checklist.filter((item) => item.type === 'BOOL' && item.id !== 'work_done');
  if (diagnostics.some((item) => item.value != null && String(item.value).trim() !== '')) {
    return diagnostics;
  }
  const fromComment = parseCheckedFromComments(ticket?.comments);
  if (fromComment.length) {
    return fromComment.map((title, i) => ({
      id: `checked_${i}`,
      title,
      type: 'BOOL',
      value: 'true',
    }));
  }
  return checklist.filter((item) => item.type === 'BOOL');
}

export function technicianCommentBody(comments = []) {
  for (let i = comments.length - 1; i >= 0; i -= 1) {
    const text = String(comments[i]?.text || '').trim();
    if (!text) continue;
    const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
    const rest = lines.filter((l) => !/^(Исход|Outcome|Проверено|Checked):/i.test(l));
    if (rest.length) return rest.join('\n');
  }
  return null;
}

export function paymentLabel(t, status) {
  const map = {
    NOT_APPLICABLE: t('service.paymentNotApplicable'),
    PENDING: t('service.paymentPending'),
    APPROVED_FOR_PAY: t('service.paymentApproved'),
    PAID: t('service.paymentPaid'),
  };
  return map[status] || status || '—';
}

export function formatTicketDate(value) {
  return formatApiDate(value, 'DD.MM.YYYY HH:mm');
}

/** Epoch seconds / Instant / ISO → ms, same rules as tech UI. */
export function parseTicketTs(value) {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  const ms = Number.isFinite(n) && String(value).trim() !== '' && !String(value).includes('-') && !String(value).includes('T')
    ? (n < 1e12 ? n * 1000 : n)
    : Date.parse(value);
  if (!Number.isFinite(ms)) return null;
  const year = new Date(ms).getFullYear();
  if (year < 2020 || year > 2100) return null;
  return ms;
}

/** Auto-ticket copy used when OP/SA creates OFFLINE / EMPTY / FULL. */
export function defaultTaskDescription(type) {
  if (type === 'OFFLINE') return 'Вернуть терминал в сеть';
  if (type === 'EMPTY') return 'Пополнить терминал';
  if (type === 'FULL') return 'Освободить терминал';
  return '';
}

function pbWord(n) {
  const abs = Math.abs(Number(n) || 0);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod10 === 1 && mod100 !== 11) return `${abs} пауэрбанк`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${abs} пауэрбанка`;
  return `${abs} пауэрбанков`;
}

/** Live EMPTY/FULL title from station snapshot, same wording as technician UI. */
export function countedActionTitle(ticket) {
  const type = ticket?.type;
  const now = Number(ticket?.pbCount);
  const target = Number(ticket?.targetPb);
  if (!Number.isFinite(now) || !Number.isFinite(target)) return '';
  if (type === 'EMPTY') return `Добавить ${pbWord(Math.max(1, target - now))}`;
  if (type === 'FULL') return `Забрать ${pbWord(Math.max(1, now - target))}`;
  return '';
}

export function composeOtherDescription(title, body) {
  const head = String(title || '').trim();
  const rest = String(body || '').trim();
  if (!head) return rest;
  return rest ? `${head}\n\n${rest}` : head;
}

export function ticketTitle(ticket) {
  const counted = countedActionTitle(ticket);
  if (counted) return counted;
  const text = String(ticket?.description || '').trim();
  if (!text) return defaultTaskDescription(ticket?.type);
  return text.split('\n')[0].trim();
}

export function ticketDescriptionBody(ticket) {
  const text = String(ticket?.description || '').trim();
  const idx = text.search(/\n/);
  if (idx < 0) return '';
  return text.slice(idx + 1).trim();
}

/**
 * SLA clock: wall time until first hand-in for review, then frozen.
 */
export function slaClockMs(ticket, now = Date.now()) {
  if (!ticket) return now;
  const fromDto = parseTicketTs(ticket.completedAt);
  if (fromDto != null) return fromDto;
  const hist = Array.isArray(ticket.history) ? ticket.history : [];
  let first = null;
  for (let i = 0; i < hist.length; i += 1) {
    const h = hist[i];
    if (!h || h.toStage !== 'DONE' || String(h.note || '') !== 'Completed') continue;
    const ts = parseTicketTs(h.at);
    if (ts == null) continue;
    if (first == null || ts < first) first = ts;
  }
  return first != null ? first : now;
}

/**
 * Overdue chip only: fact + how late vs hand-in (not vs sitting in review).
 * Absolute due stays in formatTicketDate(slaDueAt).
 */
export function slaStatusLabel(t, ticket, now = Date.now()) {
  if (!ticket || ticket.stage === 'CLOSED') return '';
  const due = parseTicketTs(ticket.slaDueAt);
  const clock = slaClockMs(ticket, now);
  const overdue = due != null && due < clock;
  if (!overdue) return '';
  const absH = Math.max(1, Math.round((clock - due) / 3600000));
  if (absH >= 48) return t('service.slaOverdueDays', { days: Math.round(absH / 24) });
  return t('service.slaOverdueHours', { hours: absH });
}

export function ticketSerials(ticket, kind) {
  if (!ticket) return [];
  const fromDto = kind === 'added'
    ? (ticket.pbAddedSerials || ticket.pbAddedIds || [])
    : (ticket.pbRemovedSerials || ticket.pbRemovedIds || []);
  const list = (Array.isArray(fromDto) ? fromDto : []).map((id) => String(id || '').trim()).filter(Boolean);
  if (list.length) return Array.from(new Set(list));
  if (ticket.powerbankId && (
    (kind === 'added' && ticket.type === 'EMPTY')
    || (kind === 'removed' && ticket.type === 'FULL')
  )) {
    return [String(ticket.powerbankId)];
  }
  return [];
}

export function inventoryReportCount(checklistValue, serials) {
  if (serials && serials.length > 0) return serials.length;
  const n = Number(checklistValue);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function isChecklistItemDone(item) {
  if (!item) return false;
  if (item.type === 'PHOTO') return (item.photoIds || []).length > 0;
  if (item.type === 'BOOL') {
    const v = (item.value || '').toString().toLowerCase();
    return v === 'true' || v === '1' || v === 'yes';
  }
  return Boolean(item.value);
}
